import React, { useRef, useState } from 'react';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { BiTrash } from 'react-icons/bi';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

import type {
  DeviceInfo,
  PostWorkDeviceInfo,
} from '@/types/InspectionArea.types';

import { TOAST_ID } from '@/constants/toastId';
import { ROUTER } from '@/constants/constants';

import Button from '@/components/common/Button';

import { Theme } from '@/styles/Theme';

import * as S from '../WorkAddCategoryListTable/style';

interface Props {
  selectedDeviceList: DeviceInfo[];
  setSelectedDeviceList: React.Dispatch<React.SetStateAction<DeviceInfo[]>>;
}

const WorkEditCategoryListTable = ({
  selectedDeviceList,
  setSelectedDeviceList,
}: Props) => {
  const [currentPrimaryCategoryId, setCurrentPrimaryCategoryId] =
    useState<number>(0);
  const [tempSelected, setTempSelected] = useState<Record<number, string>>({});

  const sectionRef = useRef<HTMLDivElement>(null);

  const handleDeleteDeviceList = (device: PostWorkDeviceInfo) => {
    const answer = window.confirm(
      device.name +
      '에 대한 점검 대상들이 삭제됩니다(복구 가능).\n정말 삭제하시겠습니까?'
    );
    if (answer) {
      const arr = selectedDeviceList
        .map((item) => {
          if (item.template_device_id === device.template_device_id) {
            // 중간에 추가된 대상은 통째로 삭제
            const info = [...item.info]
              .filter(
                (infoDetail) =>
                  infoDetail.device_id !== 0 && infoDetail.report_id !== 0
              )
              .map((infoDetail) => {
                return {
                  ...infoDetail,
                  deleted: 1,
                };
              });

            return {
              ...item,
              selected: 0,
              info,
            };
          }
          return item;
        })
        .filter(
          (item) =>
            !(
              item.template_device_id === device.template_device_id &&
              item.info.length < 1
            )
        );
      setCurrentPrimaryCategoryId(device.template_device_id);
      setSelectedDeviceList(arr);
    }
  };

  const handleTotalCount = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => {
    let total = Number(e.target.value.replace(/[^\d.-]+/g, ''));

    // Auto cap at 999 and show warning
    if (total > 999) {
      total = 999;
      toast.warning('최대 999개까지 입력 가능합니다.', {
        toastId: TOAST_ID.CATEGORY_LIST_TABLE,
      });
    }

    const array = selectedDeviceList.map((device) => {
      if (device.template_device_id === id) {
        return {
          ...device,
          total,
        };
      }
      return device;
    });

    setSelectedDeviceList(array);
  };

  const handleTempSelectedChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => {
    const onlyDigits = e.target.value.replace(/\D/g, '');
    setTempSelected((prev) => ({ ...prev, [id]: onlyDigits }));
  };

  const handleSelectedBlur = (id: number, totalCount: number) => {
    const device = selectedDeviceList.find((d) => d.template_device_id === id);
    if (!device) return;

    const raw = tempSelected[id] ?? String(device.selected);
    const num = raw === '' ? 0 : Number(raw);

    if (num > totalCount) {
      toast.error('점검수량은 전체수량을 초과할 수 없습니다.', {
        toastId: TOAST_ID.CATEGORY_LIST_TABLE,
      });
      setTempSelected((prev) => ({ ...prev, [id]: String(totalCount) }));
      return;
    }

    if (num < device.selected) {
      toast.error(
        '점검수량을 줄일 수 없습니다. 개별 항목을 삭제하려면 우측의 삭제 버튼을 사용하세요.',
        { toastId: TOAST_ID.CATEGORY_LIST_TABLE }
      );
      setTempSelected((prev) => ({ ...prev, [id]: String(device.selected) }));
      return;
    }

    if (num === device.selected) {
      return;
    }

    if (num > device.selected) {
      const diff = num - device.selected;
      const updatedList = selectedDeviceList.map((d) => {
        if (d.template_device_id === id) {
          const info = [...d.info];
          for (let i = 0; i < diff; i++) {
            info.push({
              device_id: 0,
              report_id: 0,
              device_num: '',
              location: '',
              status: '',
              deleted: 0,
            });
          }
          return {
            ...d,
            selected: num,
            info,
          };
        }
        return d;
      });

      setSelectedDeviceList(updatedList);
      setTempSelected((prev) => ({ ...prev, [id]: String(num) }));
      return;
    }
  };

  const handleDeleteDevice = (id: number, idx: number) => {
    const editArray = selectedDeviceList.map((device) => {
      if (device.template_device_id === id) {
        let info = [...device.info];

        // 중간에 추가된 대상은 통째로 삭제
        if (info[idx].device_id === 0 && info[idx].report_id === 0) {
          info.splice(idx, 1);
        } else {
          info[idx].deleted = 1;
        }

        const newSelected = device.selected - 1;
        setTempSelected((prev) => ({ ...prev, [id]: String(newSelected) }));

        return {
          ...device,
          selected: --device.selected,
          info,
        };
      }
      return device;
    });
    setSelectedDeviceList(editArray);
  };

  const handleChangeDeviceNum = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: number,
    idx: number
  ) => {
    const array = selectedDeviceList.map((device) => {
      if (device.template_device_id === id) {
        let info = [...device.info];
        info[idx].device_num = e.target.value;

        return {
          ...device,
          info,
        };
      }
      return device;
    });
    setSelectedDeviceList(array);
  };

  const handleChangeLocation = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: number,
    idx: number
  ) => {
    const array = selectedDeviceList.map((device) => {
      if (device.template_device_id === id) {
        let info = [...device.info];
        info[idx].location = e.target.value;

        return {
          ...device,
          info,
        };
      }
      return device;
    });
    setSelectedDeviceList(array);
  };

  const handleRecoverDevice = (id: number, idx: number) => {
    const editArray = selectedDeviceList.map((device) => {
      if (device.template_device_id === id) {
        let info = [...device.info];
        info[idx].deleted = 0;

        let newSelected = device.selected + 1;
        setTempSelected((prev) => ({ ...prev, [id]: String(newSelected) }));

        // 전체수량, 점검수량 같은 상황에서 복구하면 전체수량 늘리기
        if (device.selected === device.total) {
          return {
            ...device,
            total: ++device.total,
            selected: ++device.selected,
            info,
          };
        }

        return {
          ...device,
          selected: ++device.selected,
          info,
        };
      }
      return device;
    });
    setSelectedDeviceList(editArray);
  };

  const handleSelectPrimaryCategoryId = (id: number) => {
    setCurrentPrimaryCategoryId(id);

    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <S.Container>
      <S.TableWrapper width='100%' ref={sectionRef}>
        <S.LeftTable>
          <thead>
            <tr>
              <th>번호</th>
              <th>점검대상</th>
              <th>전체수량</th>
              <th>점검수량</th>
              <th>삭제</th>
            </tr>
          </thead>
          <tbody>
            {selectedDeviceList.map((device, idx) => (
              <tr key={device.template_device_id}>
                <td>{idx + 1}</td>
                <td
                  onClick={() =>
                    handleSelectPrimaryCategoryId(device.template_device_id)
                  }
                >
                  {device.name}
                </td>
                <td>
                  <S.PlusIconContainer>
                    <S.StyledInput
                      width='4.6875rem'
                      height='1.875rem'
                      type='number'
                      max={999}
                      value={device.total}
                      onChange={(e) =>
                        handleTotalCount(e, device.template_device_id)
                      }
                    />
                  </S.PlusIconContainer>
                </td>
                <td>
                  <S.PlusIconContainer>
                    <S.StyledInput
                      width='4.0625rem'
                      height='1.875rem'
                      value={tempSelected[device.template_device_id] ?? String(device.selected)}
                      onChange={(e) =>
                        handleTempSelectedChange(e, device.template_device_id)
                      }
                      onBlur={() =>
                        handleSelectedBlur(device.template_device_id, device.total)
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSelectedBlur(device.template_device_id, device.total);
                        }
                      }}
                    />
                  </S.PlusIconContainer>
                </td>
                <td>
                  <BiTrash
                    size='1.25rem'
                    cursor='pointer'
                    onClick={() => handleDeleteDeviceList(device)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </S.LeftTable>
      </S.TableWrapper>
      <S.TableWrapper width='100%'>
        <S.RightTable>
          <thead>
            <tr>
              <th style={{ width: '18%' }}>점검대상</th>
              <th style={{ width: '18%' }}>장비번호</th>
              <th style={{ width: '18%' }}>위치</th>
              <th style={{ width: '6%', textAlign: 'center' }}>사진</th>
              <th style={{ width: '6%' }}>점검표</th>
              <th style={{ width: '12%' }}>최종 점검 상태</th>
            </tr>
          </thead>
          <tbody>
            {selectedDeviceList.map((device) => {
              if (device.template_device_id === currentPrimaryCategoryId) {
                return device.info.map((info, index) => (
                  <S.StyledTr isDeleted={!!info.deleted} key={index}>
                    <td>{device.name + (index + 1)}</td>
                    <td>
                      <S.StyledInput
                        width='9.375rem'
                        height='1.875rem'
                        value={info.device_num}
                        onChange={(e) =>
                          handleChangeDeviceNum(
                            e,
                            device.template_device_id,
                            index
                          )
                        }
                      />
                    </td>
                    <td>
                      <S.StyledInput
                        width='9.375rem'
                        height='1.875rem'
                        value={info.location}
                        onChange={(e) =>
                          handleChangeLocation(
                            e,
                            device.template_device_id,
                            index
                          )
                        }
                      />
                    </td>
                    <td style={{ textAlign: 'center', verticalAlign: 'middle', width: '3.5rem' }}>
                      {!!info.thumbnail ? (
                        <div
                          style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            margin: '0 auto',
                            border: '1px solid #ddd',
                          }}
                        >
                          <img
                            src={info.thumbnail}
                            alt="thumbnail"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            margin: '0 auto',
                          }}
                        />
                      )}
                    </td>
                    <td>
                      {info.report_id !== 0 && (
                        <Link
                          to={`${ROUTER.WORK.REPORT_EDIT}/${info.report_id}`}
                          target='_blank'
                        >
                          <HiOutlineDocumentText size='1.25rem' />
                        </Link>
                      )}
                    </td>
                    <td>
                      {!!info.deleted ? (
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <Button
                            width='2.75rem'
                            height='1.4375rem'
                            fontSize='0.6875rem'
                            fontWeight={500}
                            backgroundColor={Theme.colors.GREEN}
                            onClick={() =>
                              handleRecoverDevice(
                                device.template_device_id,
                                index
                              )
                            }
                          >
                            복구
                          </Button>
                        </div>
                      ) : (
                        <BiTrash
                          size='1.25rem'
                          cursor='pointer'
                          onClick={() =>
                            handleDeleteDevice(device.template_device_id, index)
                          }
                        />
                      )}
                    </td>
                  </S.StyledTr>
                ));
              }
              return null;
            })}
          </tbody>
        </S.RightTable>
      </S.TableWrapper>
    </S.Container>
  );
};

export default WorkEditCategoryListTable;
